import { Avatar, AvatarFallback, AvatarImage } from "@radix-ui/react-avatar";
import { BusFront, Heart, MapPin, SquareArrowOutUpRight, TrainFront, Users } from "lucide-react";
import Text from "~/components/atoms/Text";
import { Card, CardContent } from "~/components/ui/card";
import SkeletonCard from "./SkeletonCard";
import convertCurrency from "~/lib/helpers/currencyConverter";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "~/components/ui/carousel";
import { useFavorite } from "~/lib/hooks/useFavourite";
import { Link } from "@remix-run/react";
import { useCompare } from "~/lib/hooks/useCompare";
import { useCurrentCurrency } from "~/lib/helpers/CurrencyHelper";
import { useState } from "react";
import { Skeleton } from "~/components/ui/skeleton";
import { isAvailableBeforeDays } from "~/lib/helpers/availabilityStringByDateHelper";
import TrueCheckWithToolTip from "../common/TrueCheckWithToolTip";
import { recordListingEngagement } from "~/lib/Telemetry";
import type { CardListingDisplay } from "~/lib/helpers/cardListingDisplay";
import {
  CARD_IMAGE_OVERLAY_BADGE_CLASS,
  CARD_IMAGE_OVERLAY_BADGE_TEXT_CLASS,
  formatPropertyDeskRangePillLabel,
} from "~/lib/helpers/cardListingDisplay";
import { normalizePropertyTypeLabel } from "~/lib/helpers/normalizePropertyType";
import ListingSpaceTypeChips from "./ListingSpaceTypeChips";

const CAROUSEL_IMAGE_HEIGHT_PX = 280;

export type PListCardProps = CardListingDisplay & {
  loading: boolean;
  link: string;
  propertyLink: string;
  isFirstVisible: boolean;
  onHoverUpdate?: (id: string | undefined) => void;
  onHover?: Function;
  buildingLink?: string;
  workspaceLink?: string;
  altText?: string;
  isPropertyPage?: boolean;
  titleText?: string;
  showFavoriteButton?: boolean;
  isFeatured?: boolean;
};

export default function PListCard({
  images,
  listingId,
  propertyId,
  workspaceId,
  buildingId,
  telemetryString,
  partnerLogo,
  showPartnerLogo,
  workspaceProviderName,
  showSpecialOfferBadge,
  offerLabel,
  buildingTitle,
  addressFormula,
  deskRangeLabel,
  spaceTypeLabel,
  spaceTypeCounts,
  closestBusStopName,
  closestBusStopTravelTime,
  closestTrainStationName,
  closestTrainStationTravelTime,
  currencyIsoCode,
  avgMonthlyCostPerWorkstation,
  priceMonthlyMin,
  priceMonthly,
  advertisedPriceMonthly,
  showDiscountedListingPrice,
  propertyTotalActiveListings,
  minDesks,
  maxDesks,
  mohLastModifiedDate,
  compareRegionSlug,
  loading,
  link,
  propertyLink,
  isFirstVisible,
  buildingLink,
  workspaceLink,
  onHoverUpdate,
  onHover,
  altText,
  isPropertyPage,
  titleText,
  showFavoriteButton = true,
  isFeatured = false,
}: PListCardProps) {
  const { currency } = useCurrentCurrency();
  const { liked, toggleFavorite } = useFavorite(listingId);
  const [chipExpandHeightDelta, setChipExpandHeightDelta] = useState(0);
  const { compareCount, isCompared, toggleCompare } = useCompare(listingId);
  const isAvailable = isAvailableBeforeDays(mohLastModifiedDate);
  const [hasRecordedEngagement, setHasRecordedEngagement] = useState(false);

  const deskRangeText = formatPropertyDeskRangePillLabel(
    minDesks,
    maxDesks,
    deskRangeLabel
  );

  const carouselImageHeight =
    CAROUSEL_IMAGE_HEIGHT_PX - chipExpandHeightDelta;

  const cardLink = isPropertyPage ? propertyLink : link;

  const recordHover = () => {
    if (onHoverUpdate) onHoverUpdate(listingId);
  };

  const clearHover = () => {
    if (onHoverUpdate) onHoverUpdate(undefined);
  };

  const handleEngagement = () => {
    if (!hasRecordedEngagement && listingId && propertyId) {
      recordListingEngagement(
        listingId,
        propertyId,
        workspaceId || "",
        buildingId || ""
      );
      setHasRecordedEngagement(true);
    }
  };

  const handleCardClick = (
    e: React.MouseEvent<HTMLDivElement> | React.KeyboardEvent<HTMLDivElement>
  ) => {
    // If triggered by keyboard, only accept Enter or Space
    if ("key" in e && e.key !== "Enter" && e.key !== " ") {
      return;
    }

    handleEngagement();
    if (cardLink) {
      window.open(cardLink, "_blank");
    }
  };

  return (
    <>
      {isFirstVisible && images?.[0] && (
        <link
          data-spinner="true"
          href={`https://ik.imagekit.io/officehub/tr:h-800,f-avif/${images[0].url}`}
          as="image"
          rel="preload"
          fetchPriority="high"
        />
      )}
      {loading ? (
        <SkeletonCard displayMode="list" />
      ) : (
        <div
          className="flex justify-center max-h-[280px] min-w-[500px] rounded-lg shadow-custom overflow-hidden group/card"
          data-otelemetry={telemetryString}
          onMouseEnter={recordHover}
          onMouseLeave={clearHover}
        >
          <Card
            role="button"
            tabIndex={0}
            onClick={handleCardClick}
            onKeyDown={handleCardClick}
            className="relative w-full overflow-hidden">

            <CardContent
              className="relative z-10 h-full flex p-0 justify-between pointer-events-none"
              onMouseEnter={() => {
                if (onHover) onHover(listingId, propertyId);
              }}
              onMouseLeave={() => {
                if (onHover) onHover(null);
              }}
            >
              {/* Left Column: Image Carousel */}
              <div className="w-[40%] relative group overflow-hidden">
                <div
                  className="relative w-full overflow-hidden transition-[height] duration-300 ease-in-out"
                  style={{
                    height: carouselImageHeight,
                    minHeight: carouselImageHeight,
                    maxHeight: carouselImageHeight,
                  }}
                >
                  <Carousel opts={{ loop: true }}>
                    <CarouselContent>
                      {images?.length
                        ? images.map((i: { url: string }, index: number) => (
                          <CarouselItem key={i.url}>
                            <div
                              className="w-full"
                              style={{ height: carouselImageHeight }}
                            >
                              <img
                                src={`https://ik.imagekit.io/officehub/tr:w-800,h-680,f-auto,f-avif/${i.url}`}
                                alt={`${altText} - Image ${index + 1}`}
                                title={titleText}
                                width={400}
                                height={carouselImageHeight}
                                className="bg-gray-300 w-full h-full object-cover"
                                loading={
                                  isFirstVisible && index === 0
                                    ? "eager"
                                    : "lazy"
                                }
                                fetchPriority={
                                  isFirstVisible && index === 0
                                    ? "high"
                                    : "low"
                                }
                              />
                            </div>
                          </CarouselItem>
                        ))
                        : null}
                    </CarouselContent>
                    <div
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}
                      className="absolute left-2 top-1/2 -translate-y-1/2 hidden group-hover/card:flex z-20 pointer-events-auto"
                    >
                      <CarouselPrevious className="static translate-y-0" />
                    </div>

                    <div
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}
                      className="absolute right-2 top-1/2 -translate-y-1/2 hidden group-hover/card:flex z-20 pointer-events-auto"
                    >
                      <CarouselNext className="static translate-y-0" />
                    </div>
                  </Carousel>

                  {/* Top-Left Overlay Badges */}
                  <div className="flex items-center gap-1.5 absolute top-4 left-4 w-[80%] flex-wrap pointer-events-none">
                    {showSpecialOfferBadge && !isPropertyPage && (
                      <div className="rounded-2xl flex items-center justify-center bg-oh-secondary pointer-events-auto">
                        <Text className="text-xs text-white py-1 px-2">
                          {offerLabel}
                        </Text>
                      </div>
                    )}

                    {isFeatured && (
                      <div className={`${CARD_IMAGE_OVERLAY_BADGE_CLASS} bg-[#23B5B5]`}>
                        <Text
                          className={`${CARD_IMAGE_OVERLAY_BADGE_TEXT_CLASS} text-white font-medium`}
                        >
                          Most popular
                        </Text>
                      </div>
                    )}
                  </div>

                  {/* Top-Right: Favorite Button */}
                  {showFavoriteButton && (
                    <button
                      type="button"
                      className="absolute right-4 top-4 z-[11] rounded-full pointer-events-auto"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleFavorite(e);
                      }}
                    >
                      <Heart
                        aria-label="Favorite button"
                        size={26}
                        color={"white"}
                        className={`${liked
                          ? "fill-oh-secondary"
                          : "fill-oh-black opacity-40"
                          } duration-300`}
                      />
                    </button>
                  )}

                  {/* Bottom-Left: Offices Button - Property Page Only */}
                  {isPropertyPage && !!propertyTotalActiveListings && propertyTotalActiveListings > 0 ? (
                    <button
                      type="button"
                      className="absolute left-0 bottom-0 pointer-events-auto inline-flex items-center justify-center rounded-tr-2xl bg-black px-3 py-1.5 shadow-sm"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleEngagement();

                        if (propertyLink) {
                          window.open(propertyLink, "_blank");
                        }
                      }}
                    >
                      <span className="inline-flex items-center justify-center gap-1.5 text-xs font-medium leading-none text-white whitespace-nowrap">
                        <span className="group-hover/card:hidden">
                          {propertyTotalActiveListings}{" "}
                          {propertyTotalActiveListings === 1 ? "Office" : "Offices"}
                        </span>

                        <span className="hidden group-hover/card:inline-flex items-center gap-1.5">
                          {propertyTotalActiveListings === 1 ? (
                            <>View Office</>
                          ) : (
                            <>View all {propertyTotalActiveListings} offices</>
                          )}

                          <SquareArrowOutUpRight
                            size={12}
                            className="flex-shrink-0"
                          />
                        </span>
                      </span>
                    </button>
                  ) : null}
                </div>
              </div>

              {/* Right Column: Details & Content */}
              <div className="w-[60%] flex flex-col overflow-hidden relative min-h-0 pointer-events-none justify-between">

                {/* Top-Right Compare - Non Property Page Only */}
                {!isPropertyPage && (
                  <div
                    className={`pointer-events-auto absolute top-3 right-4 z-20 rounded-full px-3 py-1 text-xs font-medium flex items-center gap-1.5 shadow-sm ${isCompared ? "bg-black text-white" : "bg-gray-100 text-black"
                      }`}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {/* Circle Badge: Click to Select / Deselect */}
                    <button
                      type="button"
                      aria-label={isCompared ? "Remove from compare" : "Add to compare"}
                      className={`w-4 h-4 rounded-full border text-[10px] flex items-center justify-center cursor-pointer ${isCompared
                        ? "bg-white text-black border-white font-bold hover:bg-gray-200"
                        : "bg-transparent text-black border-black"
                        } duration-300`}
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleCompare(e);
                      }}
                    >
                      {isCompared ? compareCount : ""}
                    </button>

                    {/* Text Label: Click to Navigate (if compared) or Toggle (if not) */}
                    <button
                      type="button"
                      className={`cursor-pointer ${isCompared ? "font-bold" : ""}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (isCompared) {
                          window.open(`/${compareRegionSlug}/compare`, "_blank");
                        } else {
                          toggleCompare(e);
                        }
                      }}
                    >
                      {isCompared ? "Compare Now" : "Compare"}
                    </button>
                  </div>
                )}

                <div className={`flex flex-col ${!isPropertyPage ? "gap-5" : "gap-3"} p-4 pb-2 mb-0 flex-shrink-0 flex-1`}>

                  {/* Top Content Label: Serviced Office */}
                  {spaceTypeLabel && isPropertyPage ? (
                    <span className="text-xs font-bold text-[#23B5B5] tracking-wide uppercase">
                      {normalizePropertyTypeLabel(spaceTypeLabel)}
                    </span>
                  ) : null}

                  {/* Partner Logo + Building Title & Address */}
                  <div
                    className={`grid min-w-0 gap-x-3 items-center ${partnerLogo && showPartnerLogo
                      ? "grid-cols-[auto_1fr]"
                      : "grid-cols-1"
                      }`}
                  >
                    {partnerLogo && showPartnerLogo ? (
                      <Link
                        to={workspaceLink || cardLink}
                        target="_blank"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEngagement();
                        }}
                        aria-label={`View ${workspaceProviderName || "workspace"
                          } details`}
                        className="pointer-events-auto flex-shrink-0"
                      >
                        <div className="w-12 h-12 rounded-full flex items-center justify-center p-[2px] bg-white border border-gray-100 shadow-sm">
                          <Avatar>
                            <AvatarImage
                              className="rounded-full w-[42px] h-[42px] object-scale-down"
                              src={`https://ik.imagekit.io/officehub/tr:h-128,f-avif/${partnerLogo}`}
                              alt={`Logo Of ${workspaceProviderName || "Partner Logo"
                                }`}
                              title={workspaceProviderName}
                            />

                            <AvatarFallback>
                              <Skeleton className="w-8 h-8 rounded-full" />
                            </AvatarFallback>
                          </Avatar>
                        </div>
                        {/* SEO-friendly anchor text without changing the UI */}
                        <span className="sr-only">
                          {workspaceProviderName || "Workspace provider"}
                        </span>
                      </Link>
                    ) : null}

                    <div className="flex flex-col gap-0.5 min-w-0">
                      <Link
                        to={cardLink}
                        target="_blank"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEngagement();
                        }}
                        className="pointer-events-auto block"
                        aria-label={`View ${buildingTitle}`}
                      >
                        <span
                          className={`block text-lg font-semibold truncate ${!isPropertyPage ? "max-w-[250px]" : ""
                            }`}
                        >
                          {buildingTitle}
                        </span>

                      </Link>

                      <h3 className="flex items-center gap-1 font-medium text-sm text-gray-600 min-w-0">
                        <MapPin size={16} className="text-gray-600 shrink-0" />
                        <span className="truncate">{addressFormula}</span>
                      </h3>
                    </div>
                  </div>

                  {/* Space Type Chips + View All Offices */}
                  <div className="pointer-events-auto pt-1 flex items-center gap-2 flex-nowrap">
                    <div className="flex items-center gap-2 min-w-0">
                      <ListingSpaceTypeChips
                        spaceTypeCounts={spaceTypeCounts}
                        spaceTypeLabel={spaceTypeLabel}
                        propertyLink={propertyLink}
                        onEngagement={handleEngagement}
                        onExpandHeightDeltaChange={setChipExpandHeightDelta}
                        isPropertyPage={isPropertyPage}
                        isListCard={true}
                      />
                    </div>

                    {/* View All Offices - Non Property Page Only */}
                    {!isPropertyPage &&
                      !!propertyTotalActiveListings &&
                      propertyTotalActiveListings > 0 && (
                        <Link
                          to={propertyLink || "#"}
                          target="_blank"
                          className="shrink-0 whitespace-nowrap inline-flex items-center gap-1.5 rounded-full bg-gray-100 hover:bg-gray-200 px-3 py-1.5 text-xs font-medium text-oh-black pointer-events-auto"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEngagement();
                          }}
                        >
                          <span>
                            {propertyTotalActiveListings === 1
                              ? "View Office"
                              : `View all ${propertyTotalActiveListings} offices`}
                          </span>

                          <SquareArrowOutUpRight
                            size={12}
                            className="shrink-0"
                          />
                        </Link>
                      )}
                  </div>

                  {/* Transit Information */}
                  {(closestBusStopTravelTime != null &&
                    closestBusStopName) ||
                    (closestTrainStationTravelTime != null &&
                      closestTrainStationName) ? (
                    <div className="flex flex-col gap-1 pt-1 text-xs text-gray-700 pointer-events-auto">
                      {closestTrainStationName && (
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-1.5 min-w-0 truncate">
                            <img
                              src="/assets/icons/TrainIcon.svg"
                              alt="Train"
                              className="w-4 h-4 ml-[2px] shrink-0"
                            />

                            <span className="truncate text-gray-800">
                              {closestTrainStationName}
                            </span>
                          </div>

                          <span className="shrink-0 text-gray-500 font-medium whitespace-nowrap">
                            {closestTrainStationTravelTime === 0
                              ? 1
                              : closestTrainStationTravelTime}{" "}
                            Min Walk
                          </span>
                        </div>
                      )}

                      {closestBusStopName && (
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-1.5 min-w-0 truncate">
                            <BusFront
                              className="w-5 h-5 text-oh-black shrink-0"
                              strokeWidth={1.15}
                            />

                            <span className="truncate text-gray-800">
                              {closestBusStopName}
                            </span>
                          </div>

                          <span className="shrink-0 text-gray-500 font-medium whitespace-nowrap">
                            {closestBusStopTravelTime === 0
                              ? 1
                              : closestBusStopTravelTime}{" "}
                            Min Walk
                          </span>
                        </div>
                      )}
                    </div>
                  ) : null}
                </div>

                {/* Footer Section */}
                <div className="flex justify-between items-end bg-gray-50 px-4 py-3 flex-shrink-0 mt-auto min-w-0">
                  {/* Price Info */}
                  {isPropertyPage ? (
                    <div className="flex flex-col shrink-0">
                      {avgMonthlyCostPerWorkstation ? (
                        <span className="text-xs text-gray-500 uppercase tracking-wider font-medium">
                          From
                        </span>
                      ) : null}

                      <div className="flex items-end">
                        {avgMonthlyCostPerWorkstation ? (
                          <div className="flex items-end">
                            <Text className="font-bold text-lg text-gray-800 leading-none">
                              {convertCurrency(
                                avgMonthlyCostPerWorkstation ??
                                priceMonthlyMin ??
                                0,
                                currencyIsoCode,
                                currency
                              ) + " "}
                            </Text>

                            <Text className="font-medium text-sm text-gray-600 ml-1">
                              desk/mth
                            </Text>

                            {isAvailable && (
                              <TrueCheckWithToolTip
                                date={mohLastModifiedDate}
                                isAvailable={isAvailable}
                                isCard={true}
                                isMapCard={false}
                              />
                            )}
                          </div>
                        ) : null}
                      </div>
                    </div>
                  ) : showDiscountedListingPrice ? (
                    <div className="flex flex-col">
                      <div className="flex items-end">
                        <div className="flex items-end">
                          <Text className="font-bold text-lg text-oh-secondary leading-none">
                            {convertCurrency(
                              advertisedPriceMonthly,
                              currencyIsoCode,
                              currency
                            ) + " "}
                          </Text>

                          <Text className="font-medium pl-1 text-sm text-oh-secondary">
                            /mth
                          </Text>

                          {isAvailable && (
                            <TrueCheckWithToolTip
                              date={mohLastModifiedDate}
                              isAvailable={isAvailable}
                              isCard={true}
                              isMapCard={false}
                            />
                          )}
                        </div>
                      </div>

                      <Text className="font-medium text-xs text-gray-500 line-through">
                        was {convertCurrency(
                          priceMonthly,
                          currencyIsoCode,
                          currency
                        )}{" "}
                        /mth
                      </Text>
                    </div>
                  ) : (
                    <div className="flex flex-col">
                      {priceMonthly ? (
                        <span className="text-xs text-gray-500 uppercase tracking-wider font-semibold">
                          From
                        </span>
                      ) : null}

                      <div className="flex items-center">
                        <div className="flex items-center">
                          <Text className="font-bold text-lg text-gray-800 leading-none">
                            {convertCurrency(
                              priceMonthly,
                              currencyIsoCode,
                              currency
                            ) + " "}
                          </Text>

                          <Text className="font-medium text-sm text-gray-600 ml-1">
                            /mth
                          </Text>

                          {isAvailable && (
                            <TrueCheckWithToolTip
                              date={mohLastModifiedDate}
                              isAvailable={isAvailable}
                              isCard={true}
                              isMapCard={false}
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Desk Range */}
                  <div className="flex flex-col gap-1 items-end shrink-0">
                    <span className="text-xs text-gray-500 uppercase tracking-wider font-medium">
                      Team Size
                    </span>

                    <span className="font-bold text-gray-900 flex gap-1.5 text-lg leading-none">
                      <Users
                        className="shrink-0 h-[18px] w-[18px]"
                        strokeWidth={2}
                      />

                      {deskRangeText ||
                        (minDesks && maxDesks
                          ? `${minDesks}–${maxDesks}`
                          : minDesks ?? maxDesks ?? "-")}
                    </span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </>
  );
}